/* ============================================================================
   variant-picker.js — <nko-variant-picker>, die Varianten-Swatches der
   Produktkarten (blocks/variant-picker.liquid)
   ----------------------------------------------------------------------------
   Bis M10b assets/nko-variant-picker.js (umbenannt auf das Schema
   <komponente>.js, Verhalten unveraendert; das Element heisst weiter
   `nko-variant-picker`). Jede Auswahl holt die Karte per Section Rendering
   (`section_id=product-blocks-template`) und uebernimmt Bilder, Badges und
   Radio-Zustaende daraus.

   THEME-EDITOR: Der Hoerer haengt am Element selbst, nicht am Dokument. Rendert
   der Editor die Section neu, verschwindet er mit dem alten Element, und das
   neue baut sich in connectedCallback selbst auf. disconnectedCallback bricht
   nur eine noch laufende Abfrage ab.
   ========================================================================== */

class NakedOpticsVariantPicker extends HTMLElement {
  /* Kein Konstruktor mehr: laut Spezifikation darf er weder Attribute noch
     Umgebung lesen (bei document.createElement gibt es beides noch nicht),
     `closest()` stand dort also am falschen Platz. observedAttributes
     ("color", "size") und die auskommentierten Rueckrufe waren tot — es gab
     kein attributeChangedCallback. data-section-id/-product-handle las
     niemand. */
  connectedCallback() {
    /* Ledger A13: `closest()` liefert null, sobald dieses Element ausserhalb
       einer Produktkachel steht — und jeder spaetere `this.card.querySelector`
       wirft dann beim ERSTEN Swatch-Klick. Der Rueckfall auf das Element
       selbst kann nicht null werden; die Abfragen darunter finden dann nichts
       und tun nichts, statt abzustuerzen. */
    this.card = this.closest('[data-block-type="product-card-block"]') || this;

    /* connectedCallback laeuft bei JEDEM Einhaengen, auch beim Verschieben
       im DOM. Der Listener darf nur einmal haengen, sonst liefen je Klick
       zwei Abfragen gegeneinander. */
    if (this.verbunden) return;
    this.verbunden = true;
    this.lauf = 0;
    this.abfrage = null;
    this.addEventListener('change', (e) => this.variantChanged(e));
  }

  /* Eine Antwort fuer eine Karte, die nicht mehr im Dokument steht, braucht
     niemand. Beim Verschieben (sofort wieder eingehaengt) geht dadurch nur
     die laufende Abfrage verloren; der Link der Karte stimmt schon. */
  disconnectedCallback() {
    this.abfrage?.abort();
  }

  syncAttributes(target, source, options = {}) {
    const { skip = [], preserveValueWhileFocused = true } = options;
    const skipSet = new Set(skip);

    // 1. Neue Attribute sammeln
    const incoming = new Map();
    for (const attr of source.attributes) {
      if (skipSet.has(attr.name)) continue;
      incoming.set(attr.name, attr.value);
    }

    // 2. value bei aktivem Input nicht überschreiben (User tippt evtl. gerade)
    const isFocusedInput =
      preserveValueWhileFocused &&
      document.activeElement === target &&
      'value' in target;

    // 3. Setzen/aktualisieren (nur wenn sich der Wert wirklich geändert hat)
    for (const [name, value] of incoming) {
      if (isFocusedInput && name === 'value') continue;
      if (target.getAttribute(name) !== value) {
        target.setAttribute(name, value);
      }
    }

    // 4. Entfernen, was im neuen Element nicht mehr da ist
    for (const attr of Array.from(target.attributes)) {
      if (skipSet.has(attr.name)) continue;
      if (!incoming.has(attr.name)) {
        target.removeAttribute(attr.name);
      }
    }
  }

  async variantChanged(e) {
    const auswahl = e.target;
    if (!(auswahl instanceof HTMLInputElement)) return;

    const isColorSwatch = auswahl.hasAttribute('data-color-swatch');
    // Ledger A14: `input[checked]` liest das ATTRIBUT aus dem Server-Markup,
    // also immer die urspruenglich gerenderte Auswahl. `input:checked` liest
    // den tatsaechlichen Zustand — das, was der Kunde gerade gewaehlt hat.
    const combination = Array.from(this.querySelectorAll('input:checked')).map((node) => node.id);
    const variantUrl = auswahl.getAttribute('data-variant-url');
    const productUrl = auswahl.getAttribute('data-product-url');

    /* Immer `section_id` an die Produkt- bzw. Varianten-URL. Frueher hing
       zusaetzlich `&view=blocks` daran (bzw. NUR das, bei der Varianten-URL),
       das templates/product.blocks.liquid mit genau dieser einen Section
       renderte — die Section-Rendering-API liefert dieselbe Antwort ohne
       eigenes Template (nachgemessen, bytegleich bis auf den Zeilenumbruch
       am Anfang). */
    const url = new URL(productUrl || variantUrl, location.origin);
    url.searchParams.set('section_id', 'product-blocks-template');
    if (productUrl && combination.length > 0) url.searchParams.set('option_values', combination.join(','));

    /* Der Link steht vor der Antwort fest (die Variante haengt am Radio). So
       fuehrt die Kachel auch dann auf die gewaehlte Variante, wenn die
       Abfrage scheitert — vorher blieb dann der alte Link stehen. */
    if (variantUrl) {
      this.card.querySelectorAll('[data-product-link]').forEach((link) => {
        link.setAttribute('href', variantUrl);
      });
    }

    /* Nur die LETZTE Auswahl zaehlt. Zwei schnelle Klicks starteten zwei
       Abfragen; kam die erste zuletzt an, setzte sie Bilder und Radios der
       zweiten zurueck — die Kachel zeigte Farbe A und verlinkte Variante B.
       Jede neue Auswahl bricht die vorige ab; die Laufnummer faengt eine
       Antwort, die trotzdem noch durchkommt. */
    this.abfrage?.abort();
    const abfrage = new AbortController();
    this.abfrage = abfrage;
    const lauf = ++this.lauf;

    let html;
    try {
      const response = await fetch(url, { signal: abfrage.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      html = new DOMParser().parseFromString(await response.text(), 'text/html');
    } catch {
      // Abbruch durch eine neuere Auswahl ist kein Fehler. Sonst bleibt die
      // Kachel wie sie ist (Link stimmt bereits, siehe oben). Ohne
      // `console.warn` (architektur §5.1, frueher entfernte es der Bau).
      return;
    }
    if (lauf !== this.lauf) return;

    if (isColorSwatch) {
      const images = html.querySelectorAll('[data-block-type="product-images-block"] img');
      this.card.querySelectorAll('[data-block-type="product-images-block"] img').forEach((image, index) => {
        const neu = images[index];
        if (!neu) return;
        image.setAttribute('src', neu.getAttribute('src'));
        if (neu.hasAttribute('srcset')) {
          image.setAttribute('srcset', neu.getAttribute('srcset'));
        } else {
          image.removeAttribute('srcset');
        }
      });

      const badges = html.querySelector('[data-block-type="product-images-block"] product-badges');
      const alteBadges = this.card.querySelector('[data-block-type="product-images-block"] product-badges');
      if (badges && alteBadges) alteBadges.replaceWith(badges);
    }

    for (const input of html.querySelectorAll('input')) {
      const ziel = this.querySelector(`[id="${input.id}"]`);
      if (ziel) this.syncAttributes(ziel, input, { skip: ['name', 'data-color-swatch'] });
    }
  }
}

if (!customElements.get('nko-variant-picker')) { customElements.define('nko-variant-picker', NakedOpticsVariantPicker); }

/* ----------------------------------------------------------------------------
   NUR ALS MODUL LADEN: <script src="…" type="module">, nie mit `defer`.

   Diese Datei wird ungebaut ausgeliefert. Frueher packte esbuild sie in eine
   IIFE; diesen eigenen Gueltigkeitsbereich gibt ihr jetzt der Modulbereich.
   Als klassisches Skript laegen alle Namen oben global und stiessen sich an
   denen der anderen Theme-Dateien (`FOKUSSIERBAR` steht in zwei, `starte` in
   mehreren), und `open` aus popup.js ueberschriebe `window.open`. Module
   laufen wie `defer` nach dem Parsen in Dokumentreihenfolge, aber nur einmal
   je URL und immer im strict mode.
   ---------------------------------------------------------------------------- */
